import * as React from 'react';
import { Add } from '@mui/icons-material';
import { Button, Loader, TextInput } from 'components';
import '../../AdminPanel.styles.scss';
import { useModals } from 'hooks/useModals';
import { MODALS } from 'store/contexts/ModalContext/modals';
import { StockTable } from './components/StockTable/StockTable';
import { TStock, TStockItem } from './Stock.types';
import { useAppDispatch, useAppSelector } from 'store/store';
import { getStockListAsyncThunk } from 'store/stock/stock.action';
import { resetStockList } from 'store/stock/stock.slices';
import { useInitialLoaderCondition } from 'hooks/useInitialLoaderCondition';

export const Stock: TStock = () => {
  const dispatch = useAppDispatch();
  const modals = useModals();
  const [searchValue, setSearchValue] = React.useState('');
  const {
    stockList: { data: stockList, isLoading: isStockLoading },
    createStock: { isLoading: isCreateStockLoading },
    updateStock: { isLoading: isUpdateStockLoading },
  } = useAppSelector((store) => store.stock);

  const isInitialLoadingFinished = useInitialLoaderCondition(isStockLoading);
  const isLoading =
    isStockLoading || isCreateStockLoading || isUpdateStockLoading;

  const openCreateModal = () => {
    modals.openModal(MODALS.ADMIN_STOCK_ADD);
  };

  const openEditModal = (item: TStockItem) => {
    modals.openModal(MODALS.ADMIN_STOCK_EDIT, { item });
  };

  const loadTableItems = React.useCallback(() => {
    dispatch(
      getStockListAsyncThunk({ queryParams: { page: 1, perPage: 1000 } })
    );
  }, [dispatch]);

  const filteredItems = React.useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const items = stockList?.items || [];

    if (!normalizedSearch) return items;

    return items.filter((item) => {
      const searchableValue =
        `${item.name} ${item.variation || ''}`.toLowerCase();
      return searchableValue.includes(normalizedSearch);
    });
  }, [searchValue, stockList?.items]);

  React.useEffect(() => {
    loadTableItems();
    return () => {
      dispatch(resetStockList());
    };
  }, [dispatch, loadTableItems]);

  if (!isInitialLoadingFinished) {
    return <Loader />;
  }

  return (
    <div className="admin-entity">
      <div className="admin-entity__header">
        <div>
          <h4>Stock</h4>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          marginTop: 'var(--spacing-xl)',
          alignItems: 'center',
        }}
      >
        <TextInput
          label="Search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          sx={{ maxWidth: 280 }}
        />

        <Button
          variant="contained"
          endIcon={<Add />}
          onClick={openCreateModal}
          disabled={isLoading}
        >
          Create
        </Button>
      </div>

      <StockTable items={filteredItems} onEdit={openEditModal} />
    </div>
  );
};
